import { useContext, useEffect, useState } from "react";

import { PatchSetLoanCalculator } from "../../../../../../../utils/mappers/PatchSetLoanCalculator";
import {
  Program,
  getCustomConfigByAppTheme,
} from "../../../../../../../config/config";
import { ProcessContext } from "../../../../../../../contexts/ProcessContext";
import { AssetType } from "@nemuru-eng/js-api-client";
import { removeDuplicatedObjectsFromArray } from "../../../../../../../utils/removeDuplicatedObjectsFromArray";
import { calculateMonthsDiff } from "../../../../../downPayment/DownPaymentLoanAdjust/utils/calculateMonthsDiff";
import { USED_VEHICLE_MAX_TERM } from "../../../../../downPayment/DownPaymentLoanAdjust/constants/UsedVehicleMaxTerm";
import { getMaxTermInLoanCalculator } from "./utils/getMaxTermInLoanCalculator";

export const useLoanCalculatorBase = () => {
  const {
    state: { loan, pricingByLoanId, loanCalculator, participant, asset },
    dispatch,
  } = useContext(ProcessContext);

  const [stepCalculator, setStepCalculator] = useState({
    minTerm: null,
    maxTerm: null,
    termArray: [],
    termMarks: undefined,
  });

  const allowClientToIncreasePeriod =
    loan.loan_conditions.program.name === Program.FREE_PROGRAM;

  const minQuota = getCustomConfigByAppTheme().minQuota;

  const [loanConditions, setLoanConditions] = useState({
    loanId: loan.id,
    borrowerId: participant.participantId,
    assetAmount: loan.asset_details.total_budget.amount,
    quota: loan.loan_conditions?.loan_calculator?.user_defined_quota?.amount,
    period: loan.loan_conditions.loan_calculator?.term?.period,
    principalAmount: loan.loan_conditions?.principal?.amount,
    principalCurrency: loan.loan_conditions.principal.currency,
    tae: loan.loan_conditions.loan_calculator.tae,
    tin: loan.loan_conditions.annual_interest,
    setupFee:
      loan.loan_conditions?.loan_calculator?.setup_fee?.fee_amount?.amount,
    setupFeeType: loan.loan_conditions?.loan_calculator?.setup_fee?.type,
    setupFeeCurrency:
      loan.loan_conditions.loan_calculator.setup_fee.fee_amount.currency,
    totalAmountToPay:
      loan.loan_conditions?.loan_calculator?.total_amount_paid?.amount,
    gracePeriod: loan.loan_conditions?.loan_calculator?.grace_period,
    ranges: [],
    merchantDiscountPercentage: null,
    termInterval: loan.loan_conditions.loan_calculator?.term.interval,
  });

  const { period } = loanConditions;
  const { termMarks, minTerm, maxTerm } = stepCalculator;

  useEffect(() => {
    if (loanCalculator) {
      updateStepCalculator();
    }
    if (loanCalculator && pricingByLoanId) {
      setLoanConditionsByTerm(period);
    }
  }, [loanCalculator]);

  const getMerchantDiscountByPricing = () => {
    if (!pricingByLoanId) {
      return;
    }

    let merchantDiscount = 0;
    pricingByLoanId.ranges.map((range) => {
      for (let i = range.range_start; i < range.range_end + 1; i++) {
        if (period === i) {
          return (merchantDiscount = range.merchant_discount_percentage);
        }
      }
    });
    return merchantDiscount;
  };

  const patchSetLoanCalculator = () => {
    if (
      (period !== loan.loan_conditions.loan_calculator.term.period ||
        loan.loan_conditions.principal.amount !==
          loanConditions.principalAmount) &&
      loan.loan_conditions.program.name !== Program.NO_PURCHASE
    ) {
      const loanDataToSend = {
        term_type: "",
        id: loan.id,
        principalCurrency: loanConditions.principalCurrency,
        principalAmount: loanConditions.principalAmount,
        setupFeeCurrency: loanConditions.setupFeeCurrency,
        setupFeeAmount: loanConditions.setupFee,
        setupFeeType: loanConditions.setupFeeType,
        termInterval: loanConditions.termInterval,
        loanProgramName: pricingByLoanId?.program,
        loanProgramMerchantDiscountPercentage:
          loanConditions.merchantDiscountPercentage,
        annualInterest: loanConditions.tin,
      };
      const dehydratedData = PatchSetLoanCalculator.dehydrate(
        loanDataToSend,
        period
      );
      dispatch({
        type: "REQUEST_PATCH_SET_LOAN_CALCULATOR",
        payload: dehydratedData,
      });
      return true;
    }
    return false;
  };

  const getMaxTermByMinQuota = (loanCalculator) => {
    let maxTermByMinQuota = 0;
    loanCalculator.forEach((loanCalculatorItem) => {
      if (
        loanCalculatorItem.payload.loan_calculator.user_defined_quota.amount >=
          minQuota &&
        loanCalculatorItem.payload.loan_calculator.term.period >
          maxTermByMinQuota
      ) {
        maxTermByMinQuota =
          loanCalculatorItem.payload.loan_calculator.term.period;
      }
    });
    return maxTermByMinQuota;
  };

  const updateStepCalculator = () => {
    if (!loanCalculator) {
      return;
    }

    let maxPeriodFiltered = getMaxTermByMinQuota(loanCalculator);

    if (!allowClientToIncreasePeriod) {
      maxPeriodFiltered =
        maxPeriodFiltered > period ? period : maxPeriodFiltered;
    }

    if (
      asset &&
      asset.type === AssetType.AUTO &&
      asset.is_new_vehicle !== true &&
      asset.specs.registration_date
    ) {
      const monthsDiff = calculateMonthsDiff(
        new Date(asset.specs.registration_date),
        new Date()
      );

      const maxTermInLoanCalculators =
        getMaxTermInLoanCalculator(loanCalculator);

      const maxTermForFinancing = Math.min(
        maxTermInLoanCalculators,
        USED_VEHICLE_MAX_TERM - monthsDiff
      );

      maxPeriodFiltered = Math.min(maxTermForFinancing, maxPeriodFiltered);
    }

    const termMarksArray = [];
    loanCalculator.forEach((loanCalculatorItem) => {
      if (
        loanCalculatorItem.payload.loan_calculator.term.period <=
        maxPeriodFiltered
      ) {
        termMarksArray.push({
          value: loanCalculatorItem.payload.loan_calculator.term.period,
        });
      }
    });

    const sortedLoanCalculatorArray = loanCalculator.sort((a, b) => {
      if (
        a.payload.loan_calculator.term.period <
        b.payload.loan_calculator.term.period
      ) {
        return -1;
      }
      if (
        a.payload.loan_calculator.term.period >
        b.payload.loan_calculator.term.period
      ) {
        return 1;
      }
      return 0;
    });

    const sortedTermMarksArray = termMarksArray.sort(
      (a, b) => a.value - b.value
    );

    setStepCalculator((s) => ({
      ...s,
      maxTerm: maxPeriodFiltered,
      minTerm:
        sortedLoanCalculatorArray[0]?.payload?.loan_calculator.term.period,
      termMarks: removeDuplicatedObjectsFromArray(
        sortedTermMarksArray,
        "value"
      ),
    }));
  };

  const getCurrentLoanCalculatorByTerm = (currentTerm) =>
    loanCalculator.find(
      (loanCalculator) =>
        loanCalculator.payload.loan_calculator.term.period === currentTerm
    );

  const setLoanConditionsByTerm = (term) => {
    if (!loanCalculator) {
      return;
    }

    term = parseInt(term);
    const currentLoanCalculator = getCurrentLoanCalculatorByTerm(term);
    const merchantDiscount = getMerchantDiscountByPricing();

    setLoanConditions({
      ...loanConditions,
      period: term,
      merchantDiscountPercentage: merchantDiscount,
      ...(currentLoanCalculator && {
        quota:
          currentLoanCalculator.payload.loan_calculator.user_defined_quota
            .amount,
        tae: currentLoanCalculator.payload.loan_calculator.tae,
        tin: currentLoanCalculator.payload.loan_calculator.annual_interest,
        totalAmountToPay:
          currentLoanCalculator.payload.loan_calculator.total_amount_paid
            .amount,
        setupFee:
          currentLoanCalculator.payload.loan_calculator.setup_fee.fee_amount
            .amount,
        currency: currentLoanCalculator.payload.loan_calculator.currency,
        termInterval:
          currentLoanCalculator.payload.loan_calculator.term.interval,
      }),
    });
  };

  return {
    loanConditions,
    loanCalculator,
    termMarks,
    minTerm,
    maxTerm,
    patchSetLoanCalculator,
    setLoanConditionsByTerm,
    setLoanConditions,
    getMerchantDiscountByPricing,
  };
};
